import {useState} from "react";
import PropTypes from "prop-types";
import {useT} from "../i18n/i18n";

const CARD_CLASSNAME =
    "group relative rounded-2xl border border-gray-300 dark:border-white/15 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 bg-white dark:bg-[rgb(var(--app-card))]";

const TAG_CLASSNAME =
    "inline-block px-3 py-1 text-xs font-medium rounded-full bg-[#E19F3B]/15 text-[#E19F3B] dark:bg-[#E19F3B]/20 dark:text-[#E19F3B] group-hover:bg-[#E19F3B] group-hover:text-white dark:group-hover:bg-[#E19F3B] dark:group-hover:text-white transition-all";

export default function Portfolio({language}) {
    const {t, tArray} = useT(language);
    const works = tArray("portfolio.works");

    return (
        <section className="py-16 sm:py-24">
            <div className="mb-16 sm:mb-20">
                <h1 className="text-3xl font-extrabold mb-6 leading-tight text-black">
                    {t("portfolio.title")}
                </h1>

                {/* Main Description with improved readability */}
                <p className="text-black/80 leading-relaxed">{t("portfolio.intro")}</p>
            </div>

            {/* Portfolio Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {works.map((work) => (
                    <PortfolioCard
                        key={work.id}
                        work={work}
                        viewProjectLabel={t("portfolio.viewProject")}/>
                ))}
            </div>
        </section>
    );
}

function PortfolioCard({work, viewProjectLabel}) {
    const [isHovered, setIsHovered] = useState(false);

    const handleCardClick = () => {
        if (work.link && work.link !== "#") {
            window.open(work.link, "_blank", "noreferrer");
        }
    };

    const isClickable = work.link && work.link !== "#";

    return (
        <div
            className={`${CARD_CLASSNAME} ${isClickable ? "cursor-pointer" : ""}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={handleCardClick}
            role={isClickable ? "button" : "article"}
            tabIndex={isClickable ? 0 : -1}
            onKeyDown={isClickable ? (e) => e.key === "Enter" && handleCardClick() : undefined}
        >
            {/* Image/Icon Background */}
            <div className="h-40 sm:h-48 flex items-center justify-center overflow-hidden relative" style={{ background: "linear-gradient(to bottom right, rgba(251, 146, 60, 0.08), rgba(251, 146, 60, 0.04))" }}>
                {work.image && work.image.endsWith('.jpg') || work.image?.endsWith('.png') ? (
                    <img
                        src={`/${work.image}`}
                        alt={work.title}
                        className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity duration-300"
                    />
                ) : (
                    <>
                        <div className="absolute inset-0 bg-gradient-to-t from-[rgb(var(--app-card))]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="text-5xl sm:text-6xl opacity-20 group-hover:opacity-30 transition-opacity duration-300">
                            {getIconForWork(work.id)}
                        </div>
                    </>
                )}
            </div>

            {/* Content */}
            <div className="p-5 sm:p-6 flex flex-col">
                {/* Title with improved sizing */}
                <h3 className="text-lg sm:text-xl font-bold leading-snug mb-2 text-black group-hover:text-[#E19F3B] transition-colors line-clamp-2">
                    {work.title}
                </h3>

                {/* Subtle divider for visual hierarchy */}
                <div className="h-1 w-8 bg-[#E19F3B]/25 group-hover:bg-[#E19F3B]/60 rounded-full mb-3 transition-colors" />

                {/* Description with better spacing */}
                <p className="text-sm leading-relaxed text-black/80 mb-4 flex-grow line-clamp-3">
                    {work.description}
                </p>

                {/* Tags section with better spacing */}
                <div className="flex flex-wrap gap-2 mb-4">
                    {work.tags.slice(0, 3).map((tag, idx) => (
                        <span key={idx} className={TAG_CLASSNAME}>
                            {tag}
                        </span>
                    ))}
                    {work.tags.length > 3 && (
                        <span className={TAG_CLASSNAME}>
                            +{work.tags.length - 3}
                        </span>
                    )}
                </div>

                {/* Link/CTA with improved styling */}
                {isClickable && (
                    <div className="flex items-center gap-2 font-semibold text-[#E19F3B] dark:text-[#E19F3B] group-hover:text-[#D68A2B] transition-colors pt-3 border-t border-black/10 dark:border-white/10 mt-auto">
                        <span>{viewProjectLabel}</span>
                        <span
                            aria-hidden="true"
                            className="transform group-hover:translate-x-1 transition-transform duration-200"
                        >
                            →
                        </span>
                    </div>
                )}
                {!isClickable && (
                    <div className="text-sm text-black/60 pt-3 border-t border-black/10 mt-auto">
                        Coming Soon
                    </div>
                )}
            </div>
        </div>
    );
}

PortfolioCard.propTypes = {
    work: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        tags: PropTypes.arrayOf(PropTypes.string).isRequired,
        link: PropTypes.string,
        image: PropTypes.string,
    }).isRequired,
    viewProjectLabel: PropTypes.string.isRequired,
};

/**
 * Returns emoji icons for different work types
 * These are placeholders that can be replaced with custom icons/images
 */
function getIconForWork(workId) {
    const iconMap = {
        "clinica-dentistica": "🏥",
        "this-portfolio": "💼",
        "analytics-dashboard": "📊",
        "restaurant-website": "🍽️",
        "girasole-digital": "🌻",
        "casa-mia-agenzia": "🏠",
        "studio-dentistico": "🦷",
        "bellezza-parrucchieri": "💇",
        "ecommerce-platform": "🛍️",
        "ai-tutoring": "🤖",
        "library-system": "📚",
    };
    return iconMap[workId] || "✨";
}

Portfolio.propTypes = {
    language: PropTypes.oneOf(["en", "it"]),
};
